import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isWebauthnSupported } from '../auth/webauthnHelpers';
import { navigateAway } from '../lib/tabCloseSuppression';

// REWRITTEN (2026-09-17): the actual navigator.credentials.create() call
// can't happen on THIS page anymore. CONFIRMED live: Authentik's relying
// party ID is auth.bescomapps.com, and both the browser's own WebAuthn
// security check AND (more fundamentally) Authentik's own server-side
// verification require the calling page's origin to match that exactly —
// sso.bescomapps.com is a sibling subdomain, not a match, and Authentik has
// no supported way to widen that (its Related Origin Requests support is
// browser-side only; confirmed via live testing this never reaches its own
// server-side check — see the plan file's Error 5 for the full trail).
//
// So this page's job is now just the handoff: ask our backend for a
// one-time token (proves "this real, logged-in user wants to enroll a
// passkey" without handing over the session cookie itself), then hard-
// navigate to a small, separately-hosted ceremony page that genuinely runs
// on auth.bescomapps.com, which does the actual browser ceremony and then
// redirects back to /dashboard on its own. See:
//   - authentik-gke-bescom/helm/ — the ceremony page's own static hosting
//   - accountUpdate.js's start-ceremony / ceremony-challenge / ceremony-complete
//
// Approved by the user as "Option B", explicitly as a try-it-and-see: a
// real, visible, few-seconds domain change during setup, NOT the near-
// instant redirect used elsewhere in this app for session sync — if the
// UX doesn't feel right this can still be parked in favor of SMS OTP later.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
// NOT a trailing slash — the nginx location serving this on
// auth.bescomapps.com is an exact match without one (see
// authentik-webauthn-related-origins-ingress.yaml's own comment on why: a
// trailing-slash location broke nginx's `alias` file resolution, confirmed
// live 2026-09-17). Keep these in sync.
const CEREMONY_BASE_URL = process.env.REACT_APP_WEBAUTHN_CEREMONY_URL || 'https://auth.bescomapps.com/webauthn-ceremony';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const card = {
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '420px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

export default function SetupPasskey() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const supported = isWebauthnSupported();

  // Mints a one-time token, then hard-navigates to the ceremony page on
  // auth.bescomapps.com — a real, visible cross-domain redirect (not the
  // near-instant one used for session sync elsewhere in this app). The
  // ceremony page itself redirects back to /dashboard once it's done, so
  // this component never sees the ceremony's outcome directly; any error
  // during the ceremony itself surfaces on that page, not here.
  async function handleStartPasskeySetup() {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/add-webauthn/start-ceremony`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      if (!data.token) {
        setError(data.error || 'Could not start passkey setup. Please try again.');
        setCreating(false);
        return;
      }
      navigateAway(`${CEREMONY_BASE_URL}?token=${encodeURIComponent(data.token)}`);
    } catch (e) {
      setError('Could not reach the account service.');
      setCreating(false);
    }
  }

  function handleCancel() {
    navigate('/dashboard');
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px' }}>
            Set up a Passkey
          </h1>
          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
            Sign in faster next time using your fingerprint, face, or device PIN
          </p>
        </div>

        {user && (
          <div style={{
            background: '#F0F4F8', borderRadius: '8px', padding: '10px 14px',
            marginBottom: '20px', textAlign: 'center',
          }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Setting up a passkey for
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
              {user.profile?.name || user.profile?.preferred_username}
            </p>
          </div>
        )}

        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {!supported ? (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
              This browser doesn't support passkeys. Your authenticator app (TOTP) still works as
              normal — try a recent version of Chrome, Edge, or Safari to set up a passkey.
            </p>
            <button
              onClick={handleCancel}
              style={{
                background: 'none', border: '1px solid #009FE3', color: '#009FE3',
                padding: '10px 20px', borderRadius: '8px', cursor: 'pointer',
              }}
            >
              Back to Dashboard
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={handleStartPasskeySetup}
              disabled={creating}
              style={{
                width: '100%', padding: '14px',
                background: creating ? '#9BD3F0' : 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
                color: '#fff', border: 'none', borderRadius: '10px',
                fontSize: '16px', fontWeight: '600', cursor: creating ? 'not-allowed' : 'pointer',
              }}
            >
              {creating ? 'Redirecting…' : 'Set up Passkey'}
            </button>
            <button
              onClick={handleCancel}
              disabled={creating}
              style={{
                width: '100%', marginTop: '10px', padding: '12px',
                background: 'none', border: '1px solid #D3D1C7', color: '#666',
                borderRadius: '10px', fontSize: '14px', fontWeight: '500',
                cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
